import { useState } from 'react';
import * as Plot from '@observablehq/plot';
import PlotFigure from '../../PlotFigure';
import ChartOverlay from '../common/ChartOverlay';

const colours = [
    '#1f77b4',
    '#ff7f0e',
    '#2ca02c',
    '#d62728',
    '#9467bd',
    '#8c564b',
    '#e377c2',
    '#7f7f7f',
    '#bcbd22',
    '#17becf',
];

const plotWrapperStyle = {
    marginBottom: '32px',
};

const flattenSeries = (series) => {
    const entries = Object.entries(series || {}).filter(([, points]) => Array.isArray(points) && points.length > 0);
    if (!entries.length) return null;

    const flattened = entries.flatMap(([seriesKey, points]) =>
        points.map((point) => ({
            series: seriesKey,
            x: point.x,
            y: point.y,
        }))
    );

    return { flattened, entries };
};

function ChartSection({ transformed, yScaleType = 'linear' }) {
    const [expandedPlot, setExpandedPlot] = useState(null);

    if (!transformed) return null;

    const plots = [
        {
            title: 'DNS Ping Duration',
            description: 'Latency from the device to resolve cached entries across observed hostnames.',
            yLabel: 'Seconds',
            series: transformed.dns.pingSeries,
        },
        {
            title: 'DNS Resolve Time',
            description: 'Time required to resolve hostnames via authoritative lookup.',
            yLabel: 'Seconds',
            series: transformed.dns.resolveSeries,
        },
        {
            title: 'HTTP GET Duration',
            description: 'Response time for HTTP probes to monitored URLs.',
            yLabel: 'Seconds',
            series: transformed.http,
        },
        {
            title: 'MTR Gateway Hop Duration',
            description: 'Latency observed at the first hop beyond the device.',
            yLabel: 'Seconds',
            series: transformed.mtr.gateway.series,
        },
        {
            title: 'MTR ISP Hop Duration',
            description: 'Latency within the ISP distribution network.',
            yLabel: 'Seconds',
            series: transformed.mtr.isp.series,
        },
        {
            title: 'MTR Endpoint Hop Duration',
            description: 'Latency to the final monitored endpoint.',
            yLabel: 'Seconds',
            series: transformed.mtr.endpoint.series,
        },
        {
            title: 'MTR Hop Count',
            description: 'Number of hops required to reach monitored endpoints.',
            yLabel: 'Count',
            series: transformed.mtr.hopCounts.series,
        },
        {
            title: 'Packet Loss – Gateway',
            description: 'Gateway hop packet loss across measurements.',
            yLabel: 'Percent',
            series: transformed.mtr.packetLossGateway.series,
        },
        {
            title: 'Packet Loss – ISP',
            description: 'Packet loss observed within the ISP network.',
            yLabel: 'Percent',
            series: transformed.mtr.packetLossIsp.series,
        },
        {
            title: 'Packet Loss – Endpoint',
            description: 'Packet loss reported at the final hop.',
            yLabel: 'Percent',
            series: transformed.mtr.packetLossEndpoint.series,
        },
    ];

    const available = plots
        .map((plot) => {
            const config = flattenSeries(plot.series);
            if (!config) return null;

            const plotOptions = {
                x: { type: 'time', grid: true },
                y: { grid: true, label: plot.yLabel, type: plot.yLabel === 'Seconds' ? yScaleType : 'linear' },
                color: {
                    legend: true,
                    range: colours,
                },
                marks: [
                    Plot.line(config.flattened, {
                        x: 'x',
                        y: 'y',
                        stroke: 'series',
                    }),
                    Plot.dot(config.flattened, {
                        x: 'x',
                        y: 'y',
                        fill: 'series',
                        title: (d) => `${d.series}\n${d.x.toLocaleString()}\n${d.y}`,
                    }),
                    Plot.tip(
                        config.flattened,
                        Plot.pointerX({
                            x: 'x',
                            y: 'y',
                            stroke: 'series',
                            title: (d) => `${d.series}\n${d.x.toLocaleString()}\n${plot.yLabel}: ${d.y}`,
                            pointer: 'mouse',
                            className: 'plot-tip neon-plot-tip',
                            textPadding: 12,
                            pointerSize: 14,
                            lineHeight: 1.6,
                            fontSize: 12,
                            fontWeight: '600',
                        })
                    ),
                ],
            };

            const handleExpand = () => setExpandedPlot({ ...plot, plotOptions });

            return (
                <section className="plot-section" key={plot.title} style={plotWrapperStyle}>
                    <header className="plot-section__header">
                        <div className="plot-section__header-text">
                            <h3>{plot.title}</h3>
                            {plot.description ? <p className="plot-description">{plot.description}</p> : null}
                        </div>
                        <button type="button" className="plot-expand-button" onClick={handleExpand}>
                            ⤢ Expand
                        </button>
                    </header>
                    <PlotFigure options={plotOptions} />
                </section>
            );
        })
        .filter(Boolean);
    if (!available.length) return <p>No WAN series available for the selected period.</p>;

    return (
        <>
            <div className="plots-container">{available}</div>
            {expandedPlot ? (
                <ChartOverlay
                    title={expandedPlot.title}
                    description={expandedPlot.description}
                    onClose={() => setExpandedPlot(null)}
                >
                    <PlotFigure options={expandedPlot.plotOptions} height="min(72vh, 680px)" className="chart-overlay__plot" />
                </ChartOverlay>
            ) : null}
        </>
    );
}

export default ChartSection;
